import type { OpeningTrim } from "building-types/casing/openingTrimTypes";
import type { ProfilesWithDepth } from "building-types/profiles/profileTypes";
import type {
  HeadCasingArchedSlices,
  HeadCasingEyebrowTrim,
  HeadCasingFullArch,
  HeadCasingJackArch,
  HeadCasingSlices,
  HeadCasingStone,
  HeadCasingTrim,
  KeystoneOptions,
} from "building-types/settings/headCasingSettings";
import uid from "common/utils/uid";
import { arc2ByCenterStartAngle } from "geom/arc";
import { line2Len } from "geom/line2";
import { offsetPolyLine2, offsetPolyLine2SSkFallback, polyLine2Thicken, polyLine2ThickenSSk } from "geom/offset";
import {
  planeEval,
  planeCopy,
  planeMove,
  planeRotateAroundNormal,
  planeReProjectPolyLine2,
  polyLine2OnPlane,
  planeProjectPolyLine3,
} from "geom/plane";
import type { Plane } from "geom/planeTypes";
import {
  polyLine2ConcatAll,
  polyLine2Copy,
  polyLine2EdgeEval,
  polyLine2ExtendEnd,
  polyLine2ExtendStart,
  polyLine2EdgeCount,
  polyLine2IsClosed,
  polyLine2Length,
  polyLine2Reverse,
  polyLine2ReverseCopy,
  polyLine2SetCCW,
  polyLine2SetCW,
} from "geom/polyLine2";
import type { PolyLine2, PolyLine2Polygon } from "geom/polyLine2Types";
import { polyLine2New } from "geom/polyLine2Types";
import type { PolyLineEdgeType } from "geom/polyLineTypes";
import { rect2ByTwoPoints } from "geom/rect2";
import type { Rect2 } from "geom/rect2Types";
import { headCasingExtension, headCasingRemoveCorner } from "./casingQuery";

export function headCasingFullArchProfiles(
  topLine: PolyLine2,
  rectangularOpening2: Rect2,
  trimOptions: HeadCasingFullArch,
  // @TODO(Andrew): replace this with DIST_Z_FIGHT
  offset: number = 1,
): ProfilesWithDepth {
  if (!(trimOptions)) {
    console.assert(false, "Head Casing course: options not defined!");
  }

  const profileDepth: ProfilesWithDepth = {
    profiles: [],
    trim: [],
    depth: 0,
  };

  if (!topLine) {
    return profileDepth;
  }

  const { headCasingHeight, headCasingDepth } = trimOptions;

  const fullArc = polyLine2ConcatAll(
    [[topLine[0], rectangularOpening2[1]], topLine, [topLine[topLine.length - 2], rectangularOpening2[3]]],
    false,
  );
  const trimArc = offsetPolyLine2(fullArc, offset);
  const profile = polyLine2ThickenSSk(trimArc, headCasingHeight);

  profileDepth.profiles.push(profile);
  profileDepth.depth = headCasingHeight;

  const trim: OpeningTrim[] = profileDepth.profiles.map((p) => ({
    id: uid(),
    type: "HEAD_CASING",
    name: "trim",
    rail: topLine,
    width: headCasingHeight,
    depth: headCasingDepth,
    elevationProfile: [p],
  }));

  profileDepth.trim.push(...trim);

  return profileDepth;
}

export function headCasingEyebrowTrimProfiles(
  bottomLine: PolyLine2,
  trimOptions: HeadCasingEyebrowTrim,
): ProfilesWithDepth {
  const profileDepth: ProfilesWithDepth = {
    profiles: [],
    trim: [],
    depth: 0,
  };

  const arcHeight = trimOptions.headCasingArcHeight;
  const baseHeight = trimOptions.headCasingHeight - arcHeight;

  const extension = headCasingExtension(trimOptions);

  polyLine2ExtendStart(bottomLine, extension);
  polyLine2ExtendEnd(bottomLine, extension);

  const rect = polyLine2Reverse(
    rect2ByTwoPoints(bottomLine[0], bottomLine[1], bottomLine[2], bottomLine[3] + baseHeight),
  );

  const chordLength = line2Len(rect[2], rect[3], rect[4], rect[5]);
  const radius = arcRadiusByChordLengthHeight(chordLength, arcHeight);
  const centralAngleB = arcCentralAngleByChordLengthRadius(chordLength, radius);
  const arcCenterX = (rect[2] + rect[4]) / 2;
  const arcCenterY = rect[3] - radius + arcHeight;

  const arc = arc2ByCenterStartAngle(arcCenterX, arcCenterY, rect[2], rect[3], -centralAngleB);

  const profile = polyLine2New([rect[0], rect[1], rect[2], rect[3], ...arc, rect[6], rect[7], rect[0], rect[1]]);

  profileDepth.depth = trimOptions.headCasingHeight;
  profileDepth.profiles.push(profile);

  const { headCasingHeight = 0, headCasingDepth } = trimOptions;

  const trim: OpeningTrim[] = profileDepth.profiles.map((p) => ({
    id: uid(),
    type: "HEAD_CASING",
    name: "trim",
    rail: bottomLine,
    width: headCasingHeight,
    depth: headCasingDepth,
    elevationProfile: [p],
  }));

  profileDepth.trim.push(...trim);

  return profileDepth;
}

export function headCasingArchedSlicesProfiles(
  bottomLine: PolyLine2,
  trimOptions: HeadCasingArchedSlices,
): ProfilesWithDepth {
  const profileDepth: ProfilesWithDepth = {
    profiles: [],
    trim: [],
    depth: 0,
  };

  //create base arch
  const chordLength = line2Len(bottomLine[0], bottomLine[1], bottomLine[2], bottomLine[3]);
  const arcHeight = trimOptions.headCasingArcHeight;
  const radius = arcRadiusByChordLengthHeight(chordLength, arcHeight);
  const centralAngle = arcCentralAngleByChordLengthRadius(chordLength, radius);
  const arcCenterX = (bottomLine[0] + bottomLine[2]) / 2;
  const arcCenterY = bottomLine[1] - radius + arcHeight;

  const bottomArc = arc2ByCenterStartAngle(
    arcCenterX,
    arcCenterY,
    bottomLine[0],
    bottomLine[1],
    -centralAngle,
    0.01,
    10,
    15,
  );
  let overallHeight = arcHeight;

  // Interior geometry
  if (trimOptions.headCasingInteriorDepth > 0) {
    const profile = polyLine2New([...bottomArc, bottomLine[0], bottomLine[1]]);
    profileDepth.profiles.push(profile);
    profileDepth.trim.push({
      id: uid(),
      type: "HEAD_CASING",
      name: "archedSlices.interior",
      rail: bottomLine,
      width: trimOptions.headCasingInteriorDepth,
      elevationProfile: [profile],
    });
  }

  // Arch slices
  let lastArc = bottomArc;
  for (const archSlice of trimOptions.headCasingArchedSlices || []) {
    const { height } = archSlice;
    const topArc = offsetPolyLine2(lastArc, -height);
    const profile = polyLine2New([...topArc, ...polyLine2ReverseCopy(lastArc), topArc[0], topArc[1]]);
    profileDepth.profiles.push(profile);
    profileDepth.trim.push({
      id: uid(),
      type: "HEAD_CASING",
      name: "archedSlices.slice",
      rail: bottomLine,
      width: height,
      elevationProfile: [profile],
      materialId: archSlice.material,
    });

    lastArc = topArc;
    overallHeight += height;
  }

  profileDepth.depth = overallHeight;

  return profileDepth;
}

export function headCasingTrimProfile(
  bottomLine: PolyLine2,
  arch: PolyLine2 | undefined,
  trimOptions: HeadCasingTrim,
  archEdgeTypes?: readonly PolyLineEdgeType[],
): ProfilesWithDepth {
  if (!(trimOptions)) {
    console.assert(false, "Head Casing course: options not defined!");
  }

  const profileDepth: ProfilesWithDepth = {
    profiles: [],
    trim: [],
    depth: 0,
  };

  const { headCasingHeight = 0, headCasingDepth } = trimOptions;

  const extension = headCasingExtension(trimOptions);
  let elevationProfileEdgeTypes: readonly (readonly PolyLineEdgeType[])[] | undefined;

  if (arch) {
    const profile = polyLine2Thicken(arch, headCasingHeight);
    profileDepth.profiles.push(profile);
    if (archEdgeTypes) {
      if (!(archEdgeTypes.length === polyLine2EdgeCount(arch))) {
        console.assert(false, "Head casing source edge types must correspond to arch edges");
      }
      const profileEdgeTypes: PolyLineEdgeType[] = [
        ...archEdgeTypes.slice().reverse(),
        "STRAIGHT",
        ...archEdgeTypes,
        "STRAIGHT",
      ];
      if (!(profileEdgeTypes.length === polyLine2EdgeCount(profile))) {
        console.assert(false, "Head casing edge types must correspond to profile edges");
      }
      if (profileEdgeTypes.length === polyLine2EdgeCount(profile)) {
        elevationProfileEdgeTypes = [profileEdgeTypes];
      }
    }
  } else {
    if (bottomLine.length === 4) {
      if (!headCasingRemoveCorner(trimOptions)) {
        polyLine2ExtendStart(bottomLine, extension);
        polyLine2ExtendEnd(bottomLine, extension);

        const outline = polyLine2Reverse(
          polyLine2New([
            bottomLine[0],
            bottomLine[1],
            bottomLine[2],
            bottomLine[3],
            bottomLine[2],
            bottomLine[3] + headCasingHeight,
            bottomLine[0],
            bottomLine[1] + headCasingHeight,
            bottomLine[0],
            bottomLine[1],
          ]),
        );

        profileDepth.profiles.push(outline);
      } else {
        const outline = polyLine2Reverse(
          polyLine2New([
            bottomLine[0] - extension,
            bottomLine[1],
            bottomLine[0],
            bottomLine[1],
            bottomLine[2],
            bottomLine[3],
            bottomLine[2] + extension,
            bottomLine[3],
            bottomLine[2] + extension,
            bottomLine[3] + headCasingHeight,
            bottomLine[2],
            bottomLine[3] + headCasingHeight,
            bottomLine[0],
            bottomLine[1] + headCasingHeight,
            bottomLine[0] - extension,
            bottomLine[1] + headCasingHeight,
            bottomLine[0] - extension,
            bottomLine[1],
          ]),
        );

        profileDepth.profiles.push(outline);
      }
    } else {
      if (bottomLine.length === 6) {
        if (!headCasingRemoveCorner(trimOptions)) {
          polyLine2ExtendStart(bottomLine, extension ?? 0);
          polyLine2ExtendEnd(bottomLine, extension ?? 0);

          const outline = polyLine2Reverse(
            polyLine2New([
              bottomLine[0],
              bottomLine[1],
              bottomLine[2],
              bottomLine[3],
              bottomLine[4],
              bottomLine[5],
              bottomLine[4],
              bottomLine[5] + headCasingHeight,
              bottomLine[2],
              bottomLine[3] + headCasingHeight,
              bottomLine[0],
              bottomLine[1] + headCasingHeight,
              bottomLine[0],
              bottomLine[1],
            ]),
          );

          profileDepth.profiles.push(outline);
        } else {
          const outline = polyLine2Reverse(
            polyLine2New([
              bottomLine[0] - extension,
              bottomLine[1],
              bottomLine[0],
              bottomLine[1],
              bottomLine[2],
              bottomLine[3],
              bottomLine[4],
              bottomLine[5],
              bottomLine[4] + extension,
              bottomLine[5],
              bottomLine[4] + extension,
              bottomLine[5] + headCasingHeight,
              bottomLine[4],
              bottomLine[5] + headCasingHeight,
              bottomLine[2],
              bottomLine[3] + headCasingHeight,
              bottomLine[0],
              bottomLine[1] + headCasingHeight,
              bottomLine[0] - extension,
              bottomLine[1] + headCasingHeight,
              bottomLine[0] - extension,
              bottomLine[1],
            ]),
          );

          profileDepth.profiles.push(outline);
        }
      }
    }
  }

  const trim: OpeningTrim[] = profileDepth.profiles.map((p) => ({
    id: uid(),
    type: "HEAD_CASING",
    name: "trim",
    rail: bottomLine,
    width: headCasingHeight,
    depth: headCasingDepth,
    elevationProfile: [p],
    elevationProfileEdgeTypes,
  }));

  profileDepth.trim.push(...trim);

  profileDepth.depth = headCasingHeight;
  return profileDepth;
}

export function headCasingJackArchProfiles(
  bottomLine: PolyLine2,
  openingPlane: Plane,
  jackArchOptions: HeadCasingJackArch,
): ProfilesWithDepth {
  if (!(jackArchOptions)) {
    console.assert(false, "Head Casing course: options not defined!");
  }

  const profileDepth: ProfilesWithDepth = {
    profiles: [],
    trim: [],
    depth: 0,
  };

  const profiles = jackArchLeftRightProfiles(bottomLine, planeCopy(openingPlane), jackArchOptions);
  const { bottomLineExt, leftPlane, rightPlane } = profiles;
  let { leftOutline, rightOutline } = profiles;

  const left3 = polyLine2OnPlane(leftPlane, leftOutline);
  const right3 = polyLine2OnPlane(rightPlane, rightOutline);

  leftOutline = planeProjectPolyLine3(openingPlane, left3);
  rightOutline = planeProjectPolyLine3(openingPlane, right3);

  profileDepth.profiles.push(leftOutline);
  profileDepth.trim.push({
    id: uid(),
    type: "HEAD_CASING",
    name: "jackArch.left",
    rail: bottomLineExt,
    width: jackArchOptions.headCasingHeight,
    depth: jackArchOptions.headCasingDepth,
    elevationProfile: [leftOutline],
  });
  profileDepth.profiles.push(rightOutline);
  profileDepth.trim.push({
    id: uid(),
    type: "HEAD_CASING",
    name: "jackArch.right",
    rail: bottomLineExt,
    width: jackArchOptions.headCasingHeight,
    depth: jackArchOptions.headCasingDepth,
    elevationProfile: [rightOutline],
  });
  profileDepth.depth = Math.max(jackArchOptions.keystoneHeight, jackArchOptions.headCasingHeight);
  if (jackArchOptions.includeKeystone) {
    const profile = keystoneProfile(bottomLineExt, jackArchOptions);
    profileDepth.profiles.push(profile);
    profileDepth.trim.push({
      id: uid(),
      type: "KEYSTONE",
      name: "jackArch",
      rail: bottomLineExt,
      width: jackArchOptions.keystoneHeight,
      depth: jackArchOptions.keystoneDepth,
      elevationProfile: [profile],
    });
  }

  return profileDepth;
}

export function jackArchLeftRightProfiles(
  bottomLineOrig: PolyLine2,
  openingPlane: Plane,
  jackArchOptions: HeadCasingJackArch,
): {
  leftOutline: PolyLine2;
  rightOutline: PolyLine2;
  leftPlane: Plane;
  rightPlane: Plane;
  bottomLineExt: PolyLine2;
} {
  if (!(jackArchOptions)) {
    console.assert(false, "Head Casing course: options not defined!");
  }
  let bottomLineExt = polyLine2ExtendStart(polyLine2Copy(bottomLineOrig), jackArchOptions.headCasingExtension ?? 0);
  bottomLineExt = polyLine2ExtendEnd(bottomLineExt, headCasingExtension(jackArchOptions));

  const [midX, midY] = polyLine2EdgeEval(bottomLineExt, 0, 0.5, true);
  const [sX, sY] = planeEval(openingPlane, bottomLineExt[0], bottomLineExt[1]);
  const [eX, eY] = planeEval(openingPlane, bottomLineExt[2], bottomLineExt[3]);

  const rads = Math.PI / 2 + Math.atan(jackArchOptions.keystoneSlope);
  const leftPlane = planeCopy(openingPlane);
  const rightPlane = planeCopy(openingPlane);

  // offest the planes to get better symmetry
  planeMove(leftPlane, sX - openingPlane.originX, sY - openingPlane.originY, 0);
  planeMove(rightPlane, eX - openingPlane.originX, eY - openingPlane.originY, 0);

  // rotate the planes
  planeRotateAroundNormal(leftPlane, -rads);
  planeRotateAroundNormal(rightPlane, rads);

  // build the extrusion profiles
  let leftOutline = polyLine2Reverse(
    rect2ByTwoPoints(bottomLineExt[0], bottomLineExt[1], midX, midY + jackArchOptions.headCasingHeight),
  );

  let rightOutline = polyLine2Reverse(
    rect2ByTwoPoints(midX, midY, bottomLineExt[2], bottomLineExt[3] + jackArchOptions.headCasingHeight),
  );

  const slopeOffset = (1 / jackArchOptions.keystoneSlope) * jackArchOptions.headCasingHeight;
  leftOutline[2] -= slopeOffset;
  rightOutline[4] += slopeOffset;

  // re-project the extrusion profiles onto the new planes
  leftOutline = planeReProjectPolyLine2(leftOutline, openingPlane, leftPlane);
  rightOutline = planeReProjectPolyLine2(rightOutline, openingPlane, rightPlane);

  return { leftOutline, rightOutline, leftPlane, rightPlane, bottomLineExt };
}

export function keystoneProfile(bottomLine: PolyLine2, keystone: KeystoneOptions): PolyLine2 {
  const offset = polyLine2Length(bottomLine) * 0.5 - keystone.keystoneBottomWidth * 0.5;
  const profile = polyLine2Reverse(
    rect2ByTwoPoints(
      bottomLine[0] + offset,
      bottomLine[1],
      bottomLine[2] - offset,
      bottomLine[3] + keystone.keystoneHeight,
    ),
  );
  const slopeOffset = (1 / keystone.keystoneSlope) * keystone.keystoneHeight;
  profile[2] -= slopeOffset;
  profile[4] += slopeOffset;
  return profile;
}

export function headCasingStoneProfile(bottomLine: PolyLine2, stoneOptions: HeadCasingStone): PolyLine2 {
  const extension = headCasingExtension(stoneOptions);
  polyLine2ExtendStart(bottomLine, extension);
  polyLine2ExtendEnd(bottomLine, extension);
  return polyLine2Reverse(
    rect2ByTwoPoints(bottomLine[0], bottomLine[1], bottomLine[2], bottomLine[3] + stoneOptions.headCasingHeight),
  );
}

function arcRadiusByChordLengthHeight(chordLength: number, arcHeight: number) {
  const z1 = Math.pow(chordLength, 2);
  const z2 = 8 * arcHeight;
  const z3 = arcHeight / 2;
  return z1 / z2 + z3;
}

function arcCentralAngleByChordLengthRadius(chordLength: number, radius: number) {
  const theta = Math.asin(chordLength / 2 / radius) * 2;
  return theta;
}

export function headCasingSidingProfiles(bottomLine: PolyLine2, sidingOptions: HeadCasingSlices): ProfilesWithDepth {
  if (!(sidingOptions)) {
    console.assert(false, "Head Casing course: options not defined!");
  }

  const profileDepth: ProfilesWithDepth = {
    profiles: [],
    trim: [],
    depth: 0,
  };

  let currentDepth = 0;
  let currentHeight = 0;
  let currentWidthOffset = 0;
  for (const slice of sidingOptions.headCasingSlices || []) {
    const outline = polyLine2Reverse(
      rect2ByTwoPoints(
        bottomLine[0] - slice.widthOffset * 0.5 - currentWidthOffset,
        bottomLine[1] + currentHeight,
        bottomLine[2] + slice.widthOffset * 0.5 + currentWidthOffset,
        bottomLine[3] + currentHeight + slice.height,
      ),
    );
    profileDepth.profiles.push(outline);
    profileDepth.trim.push({
      id: uid(),
      type: "HEAD_CASING",
      name: "siding",
      rail: bottomLine,
      width: slice.height,
      elevationProfile: [outline],
      materialId: slice.material,
    });

    currentHeight += slice.height;
    currentWidthOffset += slice.widthOffset * 0.5;
    currentDepth += slice.depthOffset;
  }

  profileDepth.depth = currentHeight;

  if (sidingOptions.includeKeystone) {
    const keystone = keystoneProfile(polyLine2Copy(bottomLine), sidingOptions);
    profileDepth.profiles.push(keystone);
    profileDepth.trim.push({
      id: uid(),
      type: "KEYSTONE",
      name: "siding",
      rail: bottomLine,
      width: sidingOptions.keystoneHeight,
      depth: sidingOptions.keystoneDepth,
      elevationProfile: [keystone],
    });
  }

  return profileDepth;
}

export function sideCasingProfile(
  casingLine: PolyLine2,
  sideCasingWidth: number,
  startOffset?: number,
  endOffset?: number,
): PolyLine2Polygon {
  casingLine = polyLine2Copy(casingLine);
  if (polyLine2IsClosed(casingLine)) {
    return [polyLine2SetCW(offsetPolyLine2SSkFallback(casingLine, sideCasingWidth)), polyLine2SetCCW(casingLine)];
  } else {
    casingLine = polyLine2Reverse(casingLine);
    if (startOffset) {
      polyLine2ExtendStart(casingLine, startOffset);
    }
    if (endOffset) {
      polyLine2ExtendEnd(casingLine, endOffset);
    }

    return [polyLine2SetCW(polyLine2Thicken(casingLine, -sideCasingWidth))];
  }
}

export function sideCasingProfileEdgeTypes(
  profile: PolyLine2Polygon,
  sourceEdgeTypes: readonly PolyLineEdgeType[],
): readonly (readonly PolyLineEdgeType[])[] | undefined {
  const outerLoop = profile[0];
  const innerLoop = profile[1];
  if (
    !outerLoop ||
    !innerLoop ||
    polyLine2EdgeCount(outerLoop) !== sourceEdgeTypes.length ||
    polyLine2EdgeCount(innerLoop) !== sourceEdgeTypes.length
  ) {
    return undefined;
  }

  return [sourceEdgeTypes.slice().reverse(), [...sourceEdgeTypes]];
}
